import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { SERVICES } from "@/data/services";
import useSiteImage from "@/lib/useSiteImage";
import Reveal from "./Reveal";

// Map service slug → CMS site-images slot_key. Falls back to the static
// s.img URL when no slot is defined or the slot lookup fails.
const SLUG_TO_SLOT = {
  "commercial-office":  "service_office",
  "industrial":         "service_industrial",
  "warehouse":          "service_warehouse",
  "food-factory":       "service_food",
  "cleanroom":          "service_cleanroom",
  "hospital":           "service_healthcare",
  "medical-device":     "service_medical",
  "retail-leisure":     "service_retail",
  "post-construction":  "service_post_construction",
  "school-university":  "service_school",
};

function CardMedia({ s }) {
  const slotKey = SLUG_TO_SLOT[s.slug];
  const { url: managedUrl } = useSiteImage(slotKey, s.img || "");
  const src = managedUrl || s.img;
  if (src) {
    return (
      <img
        src={src}
        alt={s.title}
        loading="lazy"
        className="w-full h-full object-cover img-mono group-hover:scale-[1.04]"
      />
    );
  }
  const Icon = s.icon;
  return (
    <div className="w-full h-full flex items-center justify-center bg-[#0E2A57] relative">
      <Icon
        size={64}
        strokeWidth={1.25}
        className="text-[#BEE7F5] relative z-10 transition-transform duration-700 group-hover:scale-110"
      />
    </div>
  );
}

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="bg-ags-cream py-28 md:py-40 border-t border-ags-hairline aaa-noise"
    >
      <div className="container-ags">
        <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-10 mb-16 md:mb-20">
          <div className="max-w-2xl">
            <div className="eyebrow mb-6">
              04 — Our Services
            </div>
            <h2 className="font-heading font-extrabold text-3xl md:text-5xl lg:text-[3.75rem] leading-[1.0] tracking-[-0.035em] text-ags-ink">
              Ten disciplines.<br />
              <span className="italic-display font-normal text-[#0E2A57]">
                One uncompromising standard.
              </span>
            </h2>
          </div>
          <div className="md:max-w-sm">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0E2A57]/55 mb-3">
              Specialised disciplines · 10
            </div>
            <p className="text-ags-text-muted text-base leading-relaxed">
              Each service line is led by trained operatives, supported by our
              in-house chemical expert, and fully RAMS-compliant for Irish sites.
            </p>
          </div>
        </Reveal>

        {/* Editorial bento grid — 12-col asymmetric, ambient card surfaces, hairline borders */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-6 md:gap-8">
          {SERVICES.map((s, i) => {
            // Asymmetric bento: card 0 = col-span-6, cards 1-2 = col-span-3,
            // cards 3-6 = col-span-3 (row 2), cards 7-9 = col-span-4 (row 3)
            const spanCls =
              i === 0
                ? "lg:col-span-6"
                : i < 3
                ? "lg:col-span-3"
                : i < 7
                ? "lg:col-span-3"
                : "lg:col-span-4";
            const aspectCls = i === 0 ? "aspect-[16/9]" : "aspect-[4/3]";
            return (
              <Reveal
                key={s.slug}
                delay={Math.min(i * 0.06, 0.5)}
                y={20}
                className={spanCls}
              >
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`service-card-${i}`}
                  className="group relative bg-white border border-ags-hairline flex flex-col cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#2BB6E5] focus:z-10 shadow-ags-ambient hover:shadow-ags-hover hover:-translate-y-1 hover:border-ags-hairline-strong transition-[transform,box-shadow,border-color] duration-500 h-full"
                >
                  <div className={`${aspectCls} overflow-hidden relative`}>
                    <CardMedia s={s} />
                    <span className="absolute top-3 left-3 font-mono text-[10px] tracking-[0.22em] uppercase text-white bg-[#0E2A57]/85 backdrop-blur-sm px-2 py-1">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div className={`${i === 0 ? "p-6 md:p-8" : "p-5 md:p-6"} flex-1 flex flex-col`}>
                    <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#2BB6E5] font-medium">
                      {s.tag}
                    </div>
                    <div className="mt-2.5 flex items-start justify-between gap-2">
                      <h3 className={`font-heading font-bold leading-tight text-[#0E2A57] tracking-[-0.02em] ${i === 0 ? "text-lg md:text-2xl" : "text-sm md:text-[15px]"}`}>
                        {s.title}
                      </h3>
                      <ArrowUpRight
                        size={i === 0 ? 20 : 14}
                        className="text-[#0E2A57] flex-shrink-0 mt-0.5 opacity-40 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-500"
                      />
                    </div>
                    <div className="mt-4 pt-4 border-t border-ags-hairline font-mono text-[9px] uppercase tracking-[0.22em] text-[#0E2A57] font-medium opacity-60 group-hover:opacity-100 group-hover:text-[#2BB6E5] transition-all">
                      Discover service →
                    </div>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
